import React, { useState } from 'react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Mail, Phone, MapPin, Send } from 'lucide-react';
import { contactData } from '../mock/spanglishData';
import { toast } from 'sonner';
import { tiktokTrack } from '../lib/tiktokPixel';

const Contact = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    level: 'beginner',
    message: ''
  });

  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
      
      const response = await fetch(`${BACKEND_URL}/api/contact`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      });

      if (!response.ok) {
        throw new Error('Failed to send request');
      }

      const data = await response.json();
      console.log('Request sent successfully:', data);

      // TikTok Pixel conversion event
      tiktokTrack('SubmitForm', {
        form_name: 'contact',
        content_type: 'lead',
        level: formData.level,
      });

      toast.success('Request sent successfully!', {
        description: 'We will contact you within 24 hours.'
      });
      
      setFormData({
        name: '',
        email: '',
        phone: '',
        level: 'beginner',
        message: ''
      });
      
    } catch (error) {
      console.error('Error:', error);
      toast.error('Failed to send request', {
        description: 'Please try again or contact us via WhatsApp.'
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section id="contact" className="py-20 bg-gradient-to-br from-blue-50 via-purple-50 to-pink-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-16">
          <h2 className="text-4xl md:text-5xl font-bold text-purple-600 mb-4">
            {contactData.title}
          </h2>
          <p className="text-xl text-purple-600 max-w-2xl mx-auto">
            {contactData.subtitle}
          </p>
        </div>

        <div className="grid md:grid-cols-2 gap-12">
          {/* Contact Info */}
          <div className="space-y-8">
            <div>
              <h3 className="text-2xl font-bold text-purple-700 mb-2">
                {contactData.formTitle}
              </h3>
              <p className="text-gray-700">
                {contactData.formSubtitle}
              </p>
            </div>

            {/* Contact Details */}
            <div className="space-y-6">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-gradient-to-br from-pink-200 to-pink-300 rounded-2xl flex items-center justify-center flex-shrink-0">
                  <Mail className="h-6 w-6 text-pink-700" />
                </div>
                <div>
                  <h4 className="font-semibold text-purple-700 mb-1">Email</h4>
                  <a href={`mailto:${contactData.email}`} className="text-gray-700 hover:text-purple-600 transition-colors">
                    {contactData.email}
                  </a>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-gradient-to-br from-purple-200 to-purple-300 rounded-2xl flex items-center justify-center flex-shrink-0">
                  <Phone className="h-6 w-6 text-purple-700" />
                </div>
                <div>
                  <h4 className="font-semibold text-purple-700 mb-1">WhatsApp & iMessage</h4>
                  <a href={`https://wa.me/${contactData.phone.replace(/[\s+]/g, '')}`} className="text-gray-700 hover:text-purple-600 transition-colors">
                    {contactData.phone}
                  </a>
                  <p className="text-sm text-gray-600">Available every day</p>
                  <p className="text-xs text-gray-500 mt-1">Also via iMessage for iOS users</p>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-gradient-to-br from-blue-200 to-blue-300 rounded-2xl flex items-center justify-center flex-shrink-0">
                  <MapPin className="h-6 w-6 text-blue-700" />
                </div>
                <div>
                  <h4 className="font-semibold text-purple-700 mb-1">Format</h4>
                  <p className="text-gray-700">100% online classes</p>
                  <p className="text-sm text-gray-600">Schedule via Google Calendar</p>
                </div>
              </div>
            </div>

            {/* Trust Badge */}
            <div className="bg-gradient-to-br from-yellow-100 to-yellow-50 rounded-2xl p-6 border-3 border-yellow-300">
              <h4 className="font-bold text-purple-700 mb-3">Why choose us?</h4>
              <ul className="space-y-2 text-sm text-gray-700">
                <li className="flex items-center gap-2">
                  <span className="text-purple-600">✓</span>
                  Reply within 24 hours
                </li>
                <li className="flex items-center gap-2">
                  <span className="text-pink-600">✓</span>
                  Personalized 1-on-1 coaching
                </li>
                <li className="flex items-center gap-2">
                  <span className="text-blue-600">✓</span>
                  Flexible scheduling on Google Calendar
                </li>
                <li className="flex items-center gap-2">
                  <span className="text-green-600">✓</span>
                  Argentine cultural immersion
                </li>
                <li className="flex items-center gap-2">
                  <span className="text-yellow-600">✓</span>
                  Returning student discount available
                </li>
              </ul>
            </div>

            {/* Social Media Section */}
            <div className="bg-gradient-to-br from-pink-100 via-purple-100 to-blue-100 rounded-2xl p-6 border-3 border-purple-300">
              <h4 className="font-bold text-purple-700 mb-2 text-center">
                Join Our Community 🌟
              </h4>
              <p className="text-sm text-gray-600 text-center mb-4">
                Follow us for daily Spanish tips, live classes and more
              </p>
              <div className="flex justify-center gap-4">
                <a 
                  href="https://www.instagram.com/holaspanglish?igsh=cWN4eTIxM3RxYnJp&utm_source=qr"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex flex-col items-center gap-2 p-3 bg-white rounded-xl shadow-sm hover:shadow-lg transition-all duration-300 hover:scale-110 group border-2 border-pink-200"
                  aria-label="Instagram"
                >
                  <div className="w-12 h-12 bg-gradient-to-br from-purple-400 via-pink-400 to-orange-400 rounded-xl flex items-center justify-center">
                    <svg className="h-6 w-6 text-white" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/>
                    </svg>
                  </div>
                  <span className="text-xs font-semibold text-purple-700 group-hover:text-pink-600">Instagram</span>
                </a>

                <a 
                  href="https://www.tiktok.com/@hola.spanglish"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex flex-col items-center gap-2 p-3 bg-white rounded-xl shadow-sm hover:shadow-lg transition-all duration-300 hover:scale-110 group border-2 border-purple-200"
                  aria-label="TikTok"
                >
                  <div className="w-12 h-12 bg-gradient-to-br from-purple-600 to-pink-600 rounded-xl flex items-center justify-center">
                    <svg className="h-6 w-6 text-white" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1-.1z"/>
                    </svg>
                  </div>
                  <span className="text-xs font-semibold text-purple-700 group-hover:text-pink-600">TikTok</span>
                </a>

                <a 
                  href="https://www.threads.com/@holaspanglish?igshid=NTc4MTIwNjQ2YQ=="
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex flex-col items-center gap-2 p-3 bg-white rounded-xl shadow-sm hover:shadow-lg transition-all duration-300 hover:scale-110 group border-2 border-blue-200"
                  aria-label="Threads"
                >
                  <div className="w-12 h-12 bg-gradient-to-br from-purple-200 to-purple-300 rounded-xl flex items-center justify-center overflow-hidden">
                    <img 
                      src="https://customer-assets.emergentagent.com/job_spanglish-hub/artifacts/n4jjnlde_image.png" 
                      alt="Threads"
                      className="h-10 w-10 object-contain"
                    />
                  </div>
                  <span className="text-xs font-semibold text-purple-700 group-hover:text-blue-600">Threads</span>
                </a>
              </div>
            </div>
          </div>

          {/* Contact Form */}
          <div className="bg-gradient-to-br from-pink-50 to-purple-50 rounded-3xl p-8 border-4 border-purple-300 shadow-xl">
            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="space-y-2">
                <Label htmlFor="name" className="text-purple-700 font-bold">
                  Full name *
                </Label>
                <Input
                  id="name"
                  name="name"
                  type="text"
                  placeholder="John Doe"
                  value={formData.name}
                  onChange={handleChange}
                  required
                  className="border-2 border-purple-300 focus:border-pink-400 focus:ring-pink-400 rounded-xl"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="email" className="text-purple-700 font-bold">
                  Email *
                </Label>
                <Input
                  id="email"
                  name="email"
                  type="email"
                  placeholder="john@example.com"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  className="border-2 border-purple-300 focus:border-pink-400 focus:ring-pink-400 rounded-xl"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="phone" className="text-purple-700 font-bold">
                  Phone
                </Label>
                <Input
                  id="phone"
                  name="phone"
                  type="tel"
                  placeholder="+1 (555) 123-4567"
                  value={formData.phone}
                  onChange={handleChange}
                  className="border-2 border-purple-300 focus:border-pink-400 focus:ring-pink-400 rounded-xl"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="level" className="text-purple-700 font-bold">
                  Current Spanish level
                </Label>
                <select
                  id="level"
                  name="level"
                  value={formData.level}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border-2 border-purple-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-pink-400 focus:border-pink-400 bg-white text-gray-700"
                >
                  <option value="beginner">Beginner (A1-A2)</option>
                  <option value="intermediate">Intermediate (B1-B2)</option>
                  <option value="advanced">Advanced (C1-C2)</option>
                  <option value="none">No prior knowledge</option>
                </select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="message" className="text-purple-700 font-bold">
                  Message
                </Label>
                <Textarea
                  id="message"
                  name="message"
                  placeholder="Tell us about your learning goals..."
                  value={formData.message}
                  onChange={handleChange}
                  rows={4}
                  className="border-2 border-purple-300 focus:border-pink-400 focus:ring-pink-400 resize-none rounded-xl"
                />
              </div>

              <Button
                type="submit"
                disabled={isSubmitting}
                className="w-full bg-gradient-to-r from-pink-300 via-purple-300 to-blue-300 hover:from-pink-400 hover:to-blue-400 text-purple-800 font-bold py-6 text-lg transition-all duration-300 hover:shadow-xl hover:scale-105 rounded-full border-3 border-purple-300"
              >
                {isSubmitting ? (
                  'Sending...'
                ) : (
                  <>
                    Send Message
                    <Send className="ml-2 h-5 w-5" />
                  </>
                )}
              </Button>

              <p className="text-xs text-gray-600 text-center">
                By submitting this form, you accept our privacy policy and terms of service.
              </p>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Contact;
